import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { getCurrencyAndRegion } from 'utils/payment'

import { PROMO_THEMES } from '../utils/promoThemes'
import { isRestrictedDomain } from '../utils/isRestrictedDomain'
import { selectCurrentUser } from '../state/selectors'
import {
  getGrowthbookUserId,
  growthBook,
  growthbookFeatures,
} from '../utils/growthbook'
import { isWithinDateRange } from '../utils/isWithinDateRange'
import { TIME_ZONE } from '../components/panels/AppBanner/consts'
import { TPromo } from '../utils/promoThemes/types'

interface GrowthBookFeature {
  enabled: boolean
  value:
    | (TPromo & {
        experiment: 'united_states' | 'standard_countries' | 'others'
      })
    | null
}

interface GrowthBookContextType {
  features: Record<string, GrowthBookFeature>
  isLoading: boolean
  initializeFeatures: () => void
  setGwAttributes: (attrs: Record<string, unknown>) => void
}

const GrowthBookContext = createContext<GrowthBookContextType>({
  features: {},
  isLoading: true,
  initializeFeatures: () => ({}),
  setGwAttributes: () => ({}),
})

interface GrowthBookProviderProps {
  children: ReactNode
}

export const GrowthBookProvider: React.FC<GrowthBookProviderProps> = ({
  children,
}: GrowthBookProviderProps) => {
  const user = useSelector(selectCurrentUser())
  const dispatch = useDispatch()
  const [gwAttributes, setGwAttributes] = React.useState({})
  const [features, setFeatures] = React.useState<
    Record<string, GrowthBookFeature>
  >({})
  const [isLoading, setIsLoading] = React.useState(true)

  const initializeFeatures = async () => {
    await growthBook.init({
      streaming: false,
    })

    const featureStates = Object.values(growthbookFeatures).reduce<
      Record<string, GrowthBookFeature>
    >(
      (acc, featureKey) => ({
        ...acc,
        [featureKey]: {
          enabled: growthBook.isOn(featureKey),
          value: growthBook.getFeatureValue(featureKey, null),
        },
      }),
      {},
    )

    setFeatures(featureStates)

    setIsLoading(false)
    dispatch({ type: 'SET_GROWTHBOOK_FEATURES', payload: featureStates })
  }

  useEffect(() => {
    if (!user) return

    const countryCode = user?.countryCode || 'unknown'
    const { currency } = getCurrencyAndRegion(countryCode)

    const attributes = {
      id: getGrowthbookUserId(),
      userId: user?.id,
      tier: user?.tier || 'unlogged',
      affiliation: user?.affiliation || false,
      planInterval: user
        ? user?.subscription?.planInterval || 'free'
        : 'unknown',
      country: countryCode,
      currency,
      shouldShowNewTierOne: false,
      ...gwAttributes,
    }

    growthBook.setAttributes(attributes)

    initializeFeatures()
  }, [user, JSON.stringify(gwAttributes)])

  const value = useMemo(
    () => ({
      features,
      isLoading,
      initializeFeatures,
      setGwAttributes,
    }),
    [features, isLoading],
  )

  return (
    <GrowthBookContext.Provider value={value}>
      {children}
    </GrowthBookContext.Provider>
  )
}

interface GrowthBookFeatureHookResult {
  isEnabled: boolean
  value: unknown
  isLoading: boolean
  initializeFeatures: () => void
  theme?: {
    [key: string]: unknown | undefined
  }
  setGwAttributes: (attrs: Record<string, unknown>) => void
}

export function useGrowthBookFeature(
  featureKey: string,
): GrowthBookFeatureHookResult {
  const context = useContext(GrowthBookContext)

  if (!context) {
    throw new Error('useGrowthBook must be used within GrowthBookProvider')
  }

  const feature = context.features[featureKey] || {}
  const { enabled = false, value } = feature

  const qaGwOff = localStorage.getItem('QA_GW_OFF') && !isRestrictedDomain()
  const isEnabled = qaGwOff
    ? false
    : enabled &&
      !!value?.enabled &&
      isWithinDateRange({
        startDate: value?.start_date,
        endDate: value?.end_date,
        timeZone: TIME_ZONE,
      })

  const theme =
    isEnabled && value?.promo_theme
      ? PROMO_THEMES?.[value?.promo_theme || '']
      : {}

  return {
    isEnabled,
    value: feature.value,
    isLoading: context.isLoading,
    theme,
    initializeFeatures: context.initializeFeatures,
    setGwAttributes: context.setGwAttributes,
  }
}
